export interface CustomerAIScores {
  automation: number;
  personalization: number;
  integration: number;
}

export interface Question {
  id: number;
  question: string;
  options: {
    text: string;
    scores: CustomerAIScores;
  }[];
}

export const questions: Question[] = [
  {
    id: 1,
    question: "How do you currently handle customer inquiries outside business hours?",
    options: [
      {
        text: "They wait until we're open - no after-hours support",
        scores: { automation: 3, personalization: 0, integration: 1 },
      },
      {
        text: "Email auto-responder, but customers wait for replies",
        scores: { automation: 2, personalization: 1, integration: 1 },
      },
      {
        text: "We have some FAQ resources customers can access",
        scores: { automation: 1, personalization: 1, integration: 2 },
      },
      {
        text: "Limited live support with extended hours",
        scores: { automation: 0, personalization: 2, integration: 1 },
      },
    ],
  },
  {
    id: 2,
    question: "What percentage of customer questions are repetitive or routine?",
    options: [
      {
        text: "Over 70% - same questions asked repeatedly",
        scores: { automation: 3, personalization: 1, integration: 1 },
      },
      {
        text: "About 50% - many common questions",
        scores: { automation: 2, personalization: 1, integration: 1 },
      },
      {
        text: "Around 30% - moderate repetition",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "Less than 20% - mostly unique inquiries",
        scores: { automation: 0, personalization: 3, integration: 1 },
      },
    ],
  },
  {
    id: 3,
    question: "How long do customers typically wait for initial responses?",
    options: [
      {
        text: "Several hours to a full day",
        scores: { automation: 3, personalization: 0, integration: 1 },
      },
      {
        text: "1-2 hours during business hours",
        scores: { automation: 2, personalization: 1, integration: 1 },
      },
      {
        text: "Within 30 minutes most of the time",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "Nearly instant responses",
        scores: { automation: 0, personalization: 1, integration: 2 },
      },
    ],
  },
  {
    id: 4,
    question: "How much time does your team spend answering the same questions?",
    options: [
      {
        text: "Most of our support time - it's overwhelming",
        scores: { automation: 3, personalization: 1, integration: 0 },
      },
      {
        text: "A significant portion - at least half our time",
        scores: { automation: 2, personalization: 1, integration: 1 },
      },
      {
        text: "Some time, but we handle it reasonably well",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "Minimal - we've streamlined most common queries",
        scores: { automation: 0, personalization: 2, integration: 2 },
      },
    ],
  },
  {
    id: 5,
    question: "Do you have customer data spread across multiple systems?",
    options: [
      {
        text: "Yes, scattered across many platforms - hard to track",
        scores: { automation: 1, personalization: 1, integration: 3 },
      },
      {
        text: "Data exists in 2-3 different systems",
        scores: { automation: 1, personalization: 1, integration: 2 },
      },
      {
        text: "Mostly centralized with some exceptions",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "Fully integrated in one system",
        scores: { automation: 1, personalization: 2, integration: 0 },
      },
    ],
  },
  {
    id: 6,
    question: "How do you currently personalize customer interactions?",
    options: [
      {
        text: "Generic responses for everyone",
        scores: { automation: 1, personalization: 3, integration: 1 },
      },
      {
        text: "Some manual personalization when time permits",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "We use customer data for basic personalization",
        scores: { automation: 1, personalization: 1, integration: 2 },
      },
      {
        text: "Highly personalized based on history and preferences",
        scores: { automation: 0, personalization: 0, integration: 3 },
      },
    ],
  },
  {
    id: 7,
    question: "What happens when a customer contacts you through different channels?",
    options: [
      {
        text: "Each channel is separate - no shared history",
        scores: { automation: 1, personalization: 2, integration: 3 },
      },
      {
        text: "Limited connection between channels",
        scores: { automation: 1, personalization: 1, integration: 2 },
      },
      {
        text: "Some integration but not seamless",
        scores: { automation: 1, personalization: 1, integration: 1 },
      },
      {
        text: "Fully connected - we see all interactions",
        scores: { automation: 1, personalization: 1, integration: 0 },
      },
    ],
  },
  {
    id: 8,
    question: "How well can your support team access customer information quickly?",
    options: [
      {
        text: "They have to search multiple places - very time-consuming",
        scores: { automation: 1, personalization: 1, integration: 3 },
      },
      {
        text: "Takes some effort to find what they need",
        scores: { automation: 1, personalization: 1, integration: 2 },
      },
      {
        text: "Mostly accessible but could be faster",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "Everything is instantly available in one place",
        scores: { automation: 1, personalization: 2, integration: 0 },
      },
    ],
  },
  {
    id: 9,
    question: "What's your biggest challenge with customer service right now?",
    options: [
      {
        text: "Can't keep up with volume - customers wait too long",
        scores: { automation: 3, personalization: 0, integration: 1 },
      },
      {
        text: "Responses feel too generic - not personal enough",
        scores: { automation: 1, personalization: 3, integration: 0 },
      },
      {
        text: "Information is scattered - hard to give complete answers",
        scores: { automation: 0, personalization: 1, integration: 3 },
      },
      {
        text: "Inconsistent experience across different touchpoints",
        scores: { automation: 1, personalization: 1, integration: 2 },
      },
    ],
  },
  {
    id: 10,
    question: "How do you measure customer satisfaction with your support?",
    options: [
      {
        text: "We don't formally track it",
        scores: { automation: 2, personalization: 1, integration: 2 },
      },
      {
        text: "Occasional surveys or feedback",
        scores: { automation: 1, personalization: 2, integration: 1 },
      },
      {
        text: "Regular tracking but manual process",
        scores: { automation: 2, personalization: 1, integration: 1 },
      },
      {
        text: "Automated tracking with real-time insights",
        scores: { automation: 0, personalization: 1, integration: 2 },
      },
    ],
  },
];
